// The Notebook (#1869): the chat's working as cells, in a large window over
// the chat. Tables, charts, composites and warehouse queries come from the
// chat's events; scripts from a composite's provenance; notes from the person.
// The person edits and re-runs cells with no agent turn, confirms or undoes
// the agent's revisions, sends their edits to the agent as one message, and
// asks for a web app from the cells they tick.
//
// Props and callbacks only: the chat page owns the controller (useNotebook)
// and the loaders.
import React, { useRef } from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { CARD_RADIUS, HAIRLINE, INNER_RADIUS } from '../../../agent/components/surface'
import DisabledReason from '../../system/DisabledReason'
import type { DatasetLoader, TextLoader } from '../../composite-table/ProvenanceModal'
import NotebookCell from './NotebookCell'
import NotebookTableCell from './NotebookTableCell'
import NotebookQueryCell from './NotebookQueryCell'
import NotebookCompositeCell from './NotebookCompositeCell'
import NotebookScriptCell from './NotebookScriptCell'
import { hasNoteText } from './notebookMessages'
import NotebookNoteCell from './NotebookNoteCell'
import NotebookSlot, { NotebookScrollRoot } from './NotebookSlot'
import type { NotebookController } from './useNotebook'

export interface NotebookDialogProps {
  notebook: NotebookController
  customer: string | undefined
  sessionId: string
  /** Why the viewer may not change the notebook (not the owner or a Manager). Undefined: they may. */
  readOnlyReason?: string
  /** Why the viewer may not run or save a warehouse query (the query card's rule). */
  queryActionReason?: string
  loadDataset: DatasetLoader
  loadScript: TextLoader
  /** Close the Notebook and scroll the chat to this tool call's card. */
  onOpenInChat: (toolCallId: string) => void
}

const NO_COMPOSER = 'The message box is not available here.'

export const NotebookDialog: React.FC<NotebookDialogProps> = ({
  notebook, customer, sessionId, readOnlyReason, queryActionReason, loadDataset, loadScript, onOpenInChat,
}) => {
  const { cells, ticked, focusCellId } = notebook
  const hasChanges = cells.some((c) => c.edit || hasNoteText(c))
  const composerReason = readOnlyReason ?? (notebook.hasComposer ? undefined : NO_COMPOSER)
  const sendReason = composerReason ?? (hasChanges ? undefined : 'Change a table, chart or query, or write a note, first.')
  const buildReason = composerReason ?? (ticked.length > 0 ? undefined : 'Tick the cells the web app should use.')
  const lastCellId = cells.length ? cells[cells.length - 1].id : null
  // The cells scroll in the dialog's content; each slot watches against it.
  const scrollRef = useRef<HTMLDivElement>(null)

  // Bring the cell "Open in notebook" named into view once the window is open.
  const handleEntered = () => {
    if (!focusCellId) return
    const el = document.querySelector(`[data-notebook-slot="${CSS.escape(focusCellId)}"]`)
    el?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  return (
    <Dialog
      open={notebook.open}
      onClose={notebook.handleClose}
      maxWidth="xl"
      fullWidth
      aria-labelledby="notebook-title"
      data-testid="notebook-dialog"
      slotProps={{
        paper: { sx: { height: '90vh', borderRadius: CARD_RADIUS } },
        transition: { onEntered: handleEntered },
      }}
    >
      <DialogTitle id="notebook-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderBottom: HAIRLINE, py: 1.25 }}>
        <Typography component="span" variant="h6" sx={{ flex: 1, minWidth: 0 }}>Notebook</Typography>
        <DisabledReason reason={readOnlyReason}>
          <Button size="small" variant="outlined" color="info" onClick={() => notebook.handleAddNote(lastCellId, '')} data-testid="notebook-add-note">
            Add note
          </Button>
        </DisabledReason>
        <DisabledReason reason={sendReason}>
          <Button size="small" variant="outlined" color="info" onClick={notebook.handleSendChanges} data-testid="notebook-send-changes">
            Send changes to the agent
          </Button>
        </DisabledReason>
        <DisabledReason reason={buildReason}>
          <Button size="small" variant="contained" color="info" onClick={notebook.handleBuildWebapp} data-testid="notebook-build-webapp">
            Build a web app from this{ticked.length > 0 ? ` (${ticked.length})` : ''}
          </Button>
        </DisabledReason>
        <IconButton aria-label="Close" onClick={notebook.handleClose} size="small" data-testid="notebook-close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent ref={scrollRef} sx={{ pt: '16px !important', backgroundColor: 'background.default' }}>
        {readOnlyReason && (
          <Alert severity="info" sx={{ mb: 1.5 }} data-testid="notebook-read-only">{readOnlyReason}</Alert>
        )}
        {notebook.loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{notebook.loadError}</Alert>}
        {notebook.saveError && <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="notebook-save-error">{notebook.saveError}</Alert>}
        {notebook.loading && cells.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        )}
        {!notebook.loading && cells.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }} data-testid="notebook-empty">
            Tables, charts, composite tables and warehouse queries the agent makes in this chat appear here, in order.
          </Typography>
        )}
        <NotebookScrollRoot.Provider value={scrollRef}>
          {cells.map((cell) => (
            // Each cell draws when it comes near the view: a big chat has hundreds (#2026).
            <NotebookSlot key={cell.id} cellId={cell.id} eager={cell.id === focusCellId}>
              {renderCell(cell)}
            </NotebookSlot>
          ))}
        </NotebookScrollRoot.Provider>
      </DialogContent>
    </Dialog>
  )

  function renderCell(cell: (typeof cells)[number]) {
    const frame = {
      cell,
      readOnlyReason,
      ticked: ticked.includes(cell.id),
      onToggleTick: () => notebook.handleToggleTick(cell.id),
      onDecide: notebook.handleDecide,
      onOpenInChat: cell.toolCallId ? () => onOpenInChat(cell.toolCallId!) : undefined,
      onAddNoteBelow: () => notebook.handleAddNote(cell.id, ''),
    }
    switch (cell.kind) {
      case 'table':
      case 'chart':
        return (
          <NotebookCell key={cell.id} {...frame}>
            <NotebookTableCell
              cell={cell}
              sessionId={sessionId}
              readOnlyReason={readOnlyReason}
              run={notebook.runs[cell.id]}
              running={!!notebook.running[cell.id]}
              runError={notebook.runErrors[cell.id] ?? ''}
              onEdit={(edit) => notebook.handleEdit(cell.id, edit)}
              onClearEdit={() => notebook.handleClearEdit(cell.id)}
              onRun={() => void notebook.handleRunTable(cell.id)}
            />
          </NotebookCell>
        )
      case 'query':
        return (
          <NotebookCell key={cell.id} {...frame}>
            <NotebookQueryCell
              // A confirmed revision is new content: start the card again.
              key={String(cell.data.id ?? cell.id)}
              cell={cell}
              customer={customer}
              sessionId={sessionId}
              actionReason={readOnlyReason ?? queryActionReason}
              onSqlChange={(sql) => notebook.handleEdit(cell.id, { sql })}
            />
          </NotebookCell>
        )
      case 'composite':
        return (
          <NotebookCell
            key={cell.id}
            {...frame}
            pendingPreview={cell.pending ? (
              <Box sx={{ mt: 1, p: 1, backgroundColor: 'background.paper', borderRadius: INNER_RADIUS }}>
                <NotebookCompositeCell data={cell.pending.data} loadDataset={loadDataset} loadScript={loadScript} />
              </Box>
            ) : undefined}
          >
            <NotebookCompositeCell data={cell.data} loadDataset={loadDataset} loadScript={loadScript} />
          </NotebookCell>
        )
      case 'script':
        return (
          <NotebookCell key={cell.id} {...frame}>
            <NotebookScriptCell path={String(cell.data.path ?? cell.title)} loadScript={loadScript} />
          </NotebookCell>
        )
      case 'note':
        return (
          <NotebookCell key={cell.id} {...frame}>
            <NotebookNoteCell
              text={cell.text ?? ''}
              readOnlyReason={readOnlyReason}
              onChange={(text) => notebook.handleEditNote(cell.id, text)}
              onDelete={() => notebook.handleDeleteNote(cell.id)}
            />
          </NotebookCell>
        )
      default:
        return null
    }
  }
}

export default NotebookDialog
